import React, { useState, forwardRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Group, Select, Text, createStyles, rem } from "@mantine/core";
import { US as Us, NL as Nl } from "country-flag-icons/react/3x2";

const languages = [
  {
    value: "en",
    label: "EN",
    icon: <Us style={{ width: rem(22), height: "auto" }} />,
  },
  {
    value: "nl",
    label: "NL",
    icon: <Nl style={{ width: rem(22), height: "auto" }} />,
  },
];

const useStyles = createStyles((theme) => ({
  selectRoot: {
    maxWidth: rem(100),
    [`@media (max-width: ${rem(375)})`]: {
      maxWidth: rem(120), // make select full width on smaller screens
    },
    [`@media (max-width: ${rem(390)})`]: {
      maxWidth: rem(110), // make select full width on smaller screens
    },
  },
  selectInput: {
    borderColor: "#e0e0e0",
    backgroundColor: "#f1f2f4",
    borderRadius: 4,
    ":focus-within": {
      borderColor: "#CCCCCC",
      backgroundColor: "#e0e0e0",
    },
    [`@media (max-width: ${rem(375)})`]: {
      width: "100%", // make input full width on smaller screens
      fontSize: rem(10),
    },
  },
  dropdown: {
    backgroundColor: "#f1f2f4", // set the background color of the dropdown
    borderRadius: 4,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)", // optional: Add subtle shadow for better visual separation
  },
  selectItem: {
    backgroundColor: "#f1f2f4", // background color for each dropdown item
    ":hover": {
      backgroundColor: "#d0d0d0", // darken the background when hovered
    },
    borderRadius: 4,
  },
}));

// Define SelectItem as a forwardRef component for the Mantine Select
const SelectItem = forwardRef(({ icon, label, ...others }, ref) => (
  <div ref={ref} {...others}>
    <Group noWrap spacing="xs">
      {icon}
      <div>
        <Text size="sm">{label}</Text>
      </div>
    </Group>
  </div>
));

SelectItem.displayName = "SelectItem";

export default function LanguageSelector() {
  const { classes } = useStyles();
  const { i18n } = useTranslation();

   // Force "nl" as default on first load
  const [language, setLanguage] = useState(() => {
    // Set nl as default if no language is set yet
    const currentLang = i18n.language || localStorage.getItem("language");
    if (!currentLang) {
      i18n.changeLanguage("nl");
      return "nl";
    }
    return currentLang;
  });


  // Initialize language immediately if needed
  useEffect(() => {
    // Initialize with Dutch if not set
    if (!i18n.language) {
      i18n.changeLanguage("nl");
    }
    
    // This ensures the dropdown shows the correct language
    const savedLanguage = i18n.language || localStorage.getItem("language") || "nl";
    setLanguage(savedLanguage);
  }, []);

  useEffect(() => {
    // Update the language when the i18n language changes
    if (i18n.language) {
      setLanguage(i18n.language);
    }
  }, [i18n.language]);

  const onChangeLang = (lang) => {
    setLanguage(lang);
    localStorage.setItem("language", lang);
    i18n.changeLanguage(lang);
  };

  return (
    <Select
      data={languages}
      itemComponent={SelectItem}
      value={language}
      onChange={onChangeLang}
      classNames={{
        root: classes.selectRoot,
        item: classes.selectItem,
        input: classes.selectInput,
        dropdown: classes.dropdown,
      }}
      icon={languages.find((item) => item.value === language)?.icon}
      transitionProps={{
        transition: "scale-y",
        duration: 80,
        timingFunction: "ease",
      }}
      size="xs"
    />
  );
}